import {
  Alert,
  Autocomplete,
  Box,
  Switch,
  TextField,
  FormHelperText,
  InputLabel,
  FormGroup,
  FormControlLabel,
  Divider,
  IconButton,
  Collapse,
  Typography,
  Stack,
  Tooltip,
  useTheme,
} from '@mui/material'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { IntegrationConfig, IntegrationConfigSettingsFollowUpBossActionPlanSMS } from '@sendhub/integrations/api-client-typescript-axios'
import * as yup from 'yup'

import { BaseSyntheticEvent, FocusEvent, useEffect, useState } from 'react'
import { updateIntegrationConfig } from '../../../store/integration-config/integration-configs.slice'
import { useAppDispatch } from '../../../store/root'
import { FormikProvider, useFormik } from 'formik'
import SelectAccount from '../../select-account'
import { FormSaveControls } from '../../save-status-indicator'
import { applyConfigUpdateError } from '../apply-config-update-error'
import { IntegrationsEnabledSwitch } from '../../integrations-enabled-switch'
import { useIntegrationsAccess } from '../../../store/integrations-access'
import { TimePicker } from '@mui/x-date-pickers'
import { Moment } from 'moment'
import moment from 'moment-timezone'

const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/
const DEFAULT_WINDOW_START = '09:00'
const DEFAULT_WINDOW_END = '17:00'

/** 'HH:mm' → minutes since local midnight. */
const hhmmToMinutes = (hhmm: string): number => {
  const [h, m] = hhmm.split(':').map(Number)
  return h * 60 + m
}
/** 'HH:mm' → friendly 'h:mm A'. */
const formatHHMM = (hhmm: string): string => moment(hhmm, 'HH:mm').format('h:mm A')

export function isFubActionPlanSmsSettings(obj: unknown): obj is IntegrationConfigSettingsFollowUpBossActionPlanSMS {
  return typeof obj === 'object' && obj !== null && 'numberFilter' in obj && ('sendWindowStart' in obj || 'scheduleFor' in obj)
}

const validationSchema = yup.object({
  enabled: yup.boolean().default(false),
  integrationId: yup.string().required(),
  settings: yup.object({
    numberFilter: yup.string().required().default('All phone numbers a contact has'),
    timezone: yup
      .string()
      .required('Timezone required')
      .default(browserTz)
      .test('valid-tz', 'Unknown timezone', (value) => !!value && !!moment.tz.zone(value)),
    sendWindowStart: yup.string().required('Required').default(DEFAULT_WINDOW_START).matches(HHMM, 'Invalid time'),
    sendWindowEnd: yup
      .string()
      .required('Required')
      .default(DEFAULT_WINDOW_END)
      .matches(HHMM, 'Invalid time')
      // An end equal to the start is an empty/ambiguous window. (An end earlier
      // than the start is allowed — it's a valid window that spans midnight.)
      .test('different-from-start', 'End time must differ from start time', function (value) {
        const start = (this.parent as { sendWindowStart?: string }).sendWindowStart
        if (!value || !start) return true
        return value !== start
      }),
  }),
  serviceAccountId: yup.string().required('Follow Up Boss Account Required').uuid(),
})

export interface ConfigureFubActionPlanSmsProps {
  integrationConfig: IntegrationConfig
}

export function ConfigureFubActionPlanSms({ integrationConfig }: ConfigureFubActionPlanSmsProps) {
  const dispatch = useAppDispatch()
  const { canEdit } = useIntegrationsAccess()

  const startingSettings = (): IntegrationConfigSettingsFollowUpBossActionPlanSMS => {
    if (!isFubActionPlanSmsSettings(integrationConfig.settings)) {
      return {
        sendWindowStart: DEFAULT_WINDOW_START,
        sendWindowEnd: DEFAULT_WINDOW_END,
        timezone: browserTz,
        numberFilter: 'All phone numbers a contact has',
      }
    }
    const s = integrationConfig.settings as IntegrationConfigSettingsFollowUpBossActionPlanSMS & { scheduleFor?: string }
    const timezone = s.timezone || browserTz

    // v2: window already stored as local HH:mm.
    if (s.sendWindowStart && HHMM.test(s.sendWindowStart)) {
      return { ...s, timezone, sendWindowStart: s.sendWindowStart, sendWindowEnd: s.sendWindowEnd }
    }
    // v1: window stored as ISO instants (UTC time-of-day) — convert to local HH:mm.
    if (s.sendWindowStart) {
      return {
        sendWindowStart: moment.tz(s.sendWindowStart, timezone).format('HH:mm'),
        sendWindowEnd: moment.tz(s.sendWindowEnd, timezone).format('HH:mm'),
        timezone,
        numberFilter: s.numberFilter,
      }
    }
    // v0: legacy single `scheduleFor` ISO — derive a 1-hour local window.
    if (s.scheduleFor) {
      const start = moment.tz(s.scheduleFor, timezone)
      return {
        sendWindowStart: start.format('HH:mm'),
        sendWindowEnd: start.clone().add(1, 'hour').format('HH:mm'),
        timezone,
        numberFilter: s.numberFilter,
      }
    }
    return { sendWindowStart: DEFAULT_WINDOW_START, sendWindowEnd: DEFAULT_WINDOW_END, timezone, numberFilter: s.numberFilter }
  }

  const [initialValues, setInitialValues] = useState({ ...integrationConfig, settings: startingSettings() })

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values, { setSubmitting, setStatus, setFieldError, setFieldTouched, resetForm }) => {
      setStatus(null)
      try {
        await dispatch(updateIntegrationConfig({ id: values.id, integrationConfig: values })).unwrap()
        resetForm({ values, status: { type: 'success' } })
      } catch (error) {
        applyConfigUpdateError(error, { setStatus, setFieldError, setFieldTouched })
      } finally {
        setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    if (!formik.dirty) {
      formik.resetForm({ values: { ...integrationConfig, settings: startingSettings() } })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integrationConfig])

  const numberFilterOptions = ['All phone numbers a contact has', 'Only the first phone number']
  const startTypingHint = 'Start typing to filter by phone type'

  const [pulseStartClock, setPulseStartClock] = useState(false)
  const [pulseEndClock, setPulseEndClock] = useState(false)
  const [showWindowHelp, setShowWindowHelp] = useState(false)
  const theme = useTheme()

  const savedTz = formik.values.settings.timezone || browserTz
  const windowTzAbbr = moment.tz(savedTz).format('z')
  const tzMismatch = savedTz !== browserTz

  const windowStart = formik.values.settings.sendWindowStart || DEFAULT_WINDOW_START
  const windowEnd = formik.values.settings.sendWindowEnd || DEFAULT_WINDOW_END
  // A window whose end is at/earlier than its start spans midnight ("overnight").
  const isOvernight = hhmmToMinutes(windowEnd) <= hhmmToMinutes(windowStart)

  return (
    <FormikProvider value={formik}>
      <form onSubmit={formik.handleSubmit}>
        <SelectAccount integrationConfig={integrationConfig} />
        <Divider sx={{ marginY: 2 }} />
        <style>
          {`
            @keyframes pulse {
              0% {
                transform: scale(0.95);
                box-shadow: 0 0 0 0 ${theme.palette.primary.main}b3;
              }

              70% {
                transform: scale(1);
                box-shadow: 0 0 0 10px ${theme.palette.primary.main}00;
              }

              100% {
                transform: scale(0.95);
                box-shadow: 0 0 0 0 ${theme.palette.primary.main}00;
              }
            }
          `}
        </style>
        {/* Send Window heading + collapsible help */}
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5, width: '100%' }}>
          <InputLabel sx={{ color: 'text.primary', fontWeight: 500 }}>Send Window</InputLabel>
          <Tooltip title={showWindowHelp ? 'Hide details' : 'Learn more'}>
            <IconButton
              size="small"
              aria-label="About the send window"
              aria-expanded={showWindowHelp}
              onClick={() => setShowWindowHelp((v) => !v)}
            >
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          The hours texts are allowed to go out, in the timezone below.
        </Typography>
        <Collapse in={showWindowHelp}>
          <Alert severity="info" icon={false} sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              How the send window works
            </Typography>
            <Typography variant="body2" paragraph>
              When one of your Action Plans reaches a Task step, the task gets created in Follow Up Boss at unpredictable times of day. This
              integration picks up those Tasks if their Type is set to "Text" and sends the name of the task as the message &mdash; but only
              during the hours you set here.
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
              What happens to each text:
            </Typography>
            <Box component="ul" sx={{ mt: 0, mb: 1.5, pl: 2.5, '& li': { mb: 0.5 } }}>
              <li>Triggered before the Send Window &rarr; scheduled to be sent at the next start time the same day.</li>
              <li>Triggered during the Send Window &rarr; sent right away.</li>
              <li>Triggered after the Send Window &rarr; scheduled to be sent at the start time the next day.</li>
            </Box>
            <Typography variant="body2" paragraph sx={{ fontStyle: 'italic' }}>
              Example: with a 9:00&nbsp;AM&ndash;6:00&nbsp;PM Send Window, a text triggered at 7:30&nbsp;AM goes out at 9:00&nbsp;AM; one
              triggered at 2:00&nbsp;PM goes out immediately; one triggered at 11:00&nbsp;<em>PM</em> goes out at 9:00&nbsp;AM the next day.
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Texting overnight?</strong> Set the end time earlier than the start (for example 8:00&nbsp;PM to 7:00&nbsp;AM) and the
              Send Window will run through the night.
            </Typography>
            <Typography variant="body2" sx={{ mb: 0 }}>
              <strong>One exception:</strong> if you create a Follow Up Boss task (with Type=Text) with a specific date <em>and</em> time
              yourself, that exact time is used even if it&rsquo;s outside the Send Window. Action Plan-created Tasks don&rsquo;t have a set
              time, so they always stay inside your Send Window.
            </Typography>
          </Alert>
        </Collapse>

        {/* Start + End time pickers, sharing a single row. */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, width: '100%' }}>
          <Box sx={{ flex: 1 }}>
            <TimePicker
              disabled={!canEdit}
              label={`Start`}
              inputFormat={`hh:mm A [${windowTzAbbr}]`}
              value={moment(formik.values.settings.sendWindowStart, 'HH:mm')}
              onChange={(newValue: Moment | null) => {
                if (newValue && newValue.isValid()) formik.setFieldValue('settings.sendWindowStart', newValue.format('HH:mm'))
              }}
              onClose={() => setPulseStartClock(false)}
              onAccept={() => setPulseStartClock(false)}
              onOpen={() => setPulseStartClock(false)}
              onError={() => setPulseStartClock(true)}
              InputAdornmentProps={{
                style: {
                  borderRadius: '50%',
                  height: '20px',
                  width: '20px',
                  justifyContent: 'flex-end',
                  paddingRight: '2px',
                  marginRight: '-2px',
                  ...(pulseStartClock ? { animation: 'pulse 1.2s infinite' } : {}),
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  onFocus={() => setPulseStartClock(true)}
                  onBlur={() => setPulseStartClock(false)}
                  id={'settings.sendWindowStart'}
                  name={'settings.sendWindowStart'}
                  sx={{ width: '100%' }}
                  error={formik.touched.settings?.sendWindowStart && Boolean(formik.errors.settings?.sendWindowStart)}
                  helperText={(formik.touched.settings?.sendWindowStart && formik.errors.settings?.sendWindowStart) || ' '}
                />
              )}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <TimePicker
              disabled={!canEdit}
              label={`End`}
              inputFormat={`hh:mm A [${windowTzAbbr}]`}
              value={moment(formik.values.settings.sendWindowEnd, 'HH:mm')}
              onChange={(newValue: Moment | null) => {
                if (newValue && newValue.isValid()) formik.setFieldValue('settings.sendWindowEnd', newValue.format('HH:mm'))
              }}
              onClose={() => setPulseEndClock(false)}
              onAccept={() => setPulseEndClock(false)}
              onOpen={() => setPulseEndClock(false)}
              onError={() => setPulseEndClock(true)}
              InputAdornmentProps={{
                style: {
                  borderRadius: '50%',
                  height: '20px',
                  width: '20px',
                  justifyContent: 'flex-end',
                  paddingRight: '2px',
                  marginRight: '-2px',
                  ...(pulseEndClock ? { animation: 'pulse 1.2s infinite' } : {}),
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  onFocus={() => setPulseEndClock(true)}
                  onBlur={() => setPulseEndClock(false)}
                  id={'settings.sendWindowEnd'}
                  name={'settings.sendWindowEnd'}
                  sx={{ width: '100%' }}
                  error={formik.touched.settings?.sendWindowEnd && Boolean(formik.errors.settings?.sendWindowEnd)}
                  helperText={(formik.touched.settings?.sendWindowEnd && formik.errors.settings?.sendWindowEnd) || ' '}
                />
              )}
            />
          </Box>
        </Box>
        {isOvernight && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -1, mb: 2 }}>
            Overnight window: {formatHHMM(windowStart)} to {formatHHMM(windowEnd)} {windowTzAbbr} (spans midnight).
          </Typography>
        )}
        <Autocomplete
          sx={{ marginBottom: tzMismatch ? 1 : 2 }}
          disableClearable
          autoHighlight
          disabled={!canEdit}
          options={moment.tz.names()}
          value={savedTz}
          onChange={(event, value) => formik.setFieldValue('settings.timezone', value)}
          renderInput={(params) => (
            <TextField
              {...params}
              id={'settings.timezone'}
              name={'settings.timezone'}
              label="Timezone"
              error={formik.touched.settings?.timezone && Boolean(formik.errors.settings?.timezone)}
              helperText={
                (formik.touched.settings?.timezone && formik.errors.settings?.timezone) ||
                'Send window times are interpreted in this timezone (from your Follow Up Boss account).'
              }
            />
          )}
        />
        {tzMismatch && (
          <Alert severity="warning" sx={{ marginBottom: 2 }}>
            Your browser timezone ({browserTz}) differs from the saved timezone ({savedTz}). The send window times above are interpreted in{' '}
            {savedTz}.
          </Alert>
        )}
        <Autocomplete
          sx={{ marginBottom: 2 }}
          disableClearable
          openOnFocus
          freeSolo
          disabled={!canEdit}
          selectOnFocus
          onBlur={(event: FocusEvent<HTMLInputElement>) => {
            // If user types in the Autocomplete but doesn't select an option, use what they typed onBlur
            const match = [...event.target.value.matchAll(/Only phones where type is: "(.*)"/g), null][0]
            const value = match ? match[1] : event.target.value
            if (value.trim() && value !== formik.values.settings.numberFilter) formik.setFieldValue('settings.numberFilter', value)
          }}
          id="numberFilter"
          value={formik.values.settings.numberFilter}
          onChange={(event, value) => formik.setFieldValue('settings.numberFilter', value)}
          options={[...numberFilterOptions]}
          getOptionLabel={(option) =>
            [...numberFilterOptions, startTypingHint].includes(option) ? option : `Only phones where type is: "${option}"`
          }
          renderInput={(params) => {
            const originalOnChange = params.inputProps.onChange || (() => undefined)
            return (
              <TextField
                {...params}
                inputProps={{
                  ...params.inputProps,
                  onChange: (event: BaseSyntheticEvent<Event, HTMLInputElement, HTMLInputElement>) => {
                    const match = [...event.currentTarget.value.matchAll(/Only phones where type is: "(.*)"/g), null][0]
                    if (match) {
                      event.target.value = match[1]
                    }
                    originalOnChange(event)
                  },
                }}
                name="numberFilter"
                label="Which Contact Number(s) To Send To"
                error={formik.touched.settings?.numberFilter && Boolean(formik.errors.settings?.numberFilter)}
                helperText={
                  (formik.touched.settings?.numberFilter && formik.errors.settings?.numberFilter) ||
                  `Specifies which of the contact's phone number(s) to send to.`
                }
              />
            )
          }}
          getOptionDisabled={(option) => [startTypingHint].includes(option)}
          filterOptions={(options, params) => {
            if (params.inputValue.trim() && !numberFilterOptions.includes(params.inputValue)) return [...options, params.inputValue]
            if (!numberFilterOptions.includes(formik.values.settings.numberFilter)) return [...options, formik.values.settings.numberFilter]

            return [...options, startTypingHint]
          }}
        />

        <FormGroup>
          <IntegrationsEnabledSwitch />
        </FormGroup>
        <FormSaveControls />
      </form>
    </FormikProvider>
  )
}
